import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import {
  reservationApi,
  tableGroupApi,
  waitlistApi,
  reservationSettingsApi,
} from '../api/reservationApi';
import {
  RESERVATION_EVENTS,
  TABLE_GROUP_EVENTS,
  WAITLIST_EVENTS,
} from '../constants/socketEvents';
import { todayLocalIso } from '../utils/dateLocal';
import type {
  Reservation,
  ReservationRequest,
  TableGroup,
  TableGroupRequest,
  WaitlistEntry,
  WaitlistRequest,
  ReservationSettings,
  ReservationDailyStats,
  WaitlistStats,
} from '../types/reservation.types';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface ReservationContextType {
  // State
  reservations: Reservation[];
  waitlist: WaitlistEntry[];
  tableGroups: TableGroup[];
  settings: ReservationSettings | null;
  dailyStats: ReservationDailyStats | null;
  waitlistStats: WaitlistStats | null;
  selectedDate: string;
  loading: boolean;
  error: string | null;

  // Reservation actions
  setSelectedDate: (date: string) => void;
  createReservation: (data: ReservationRequest) => Promise<Reservation>;
  updateReservation: (id: number, data: Partial<ReservationRequest>) => Promise<Reservation>;
  confirmReservation: (id: number) => Promise<Reservation>;
  seatReservation: (id: number) => Promise<Reservation>;
  completeReservation: (id: number) => Promise<Reservation>;
  cancelReservation: (id: number, reason?: string) => Promise<Reservation>;
  markNoShow: (id: number) => Promise<Reservation>;

  // Table group actions
  createTableGroup: (data: TableGroupRequest) => Promise<TableGroup>;
  closeTableGroup: (groupId: number) => Promise<TableGroup>;
  addTableToGroup: (groupId: number, tableId: number) => Promise<TableGroup>;
  removeTableFromGroup: (groupId: number, tableId: number) => Promise<TableGroup>;
  linkBillingToGroup: (groupId: number, orderLineId: number) => Promise<TableGroup>;

  // Waitlist actions
  addToWaitlist: (data: WaitlistRequest) => Promise<WaitlistEntry>;
  notifyWaitlistCustomer: (id: number, tableId?: number, tableGroupId?: number) => Promise<WaitlistEntry>;
  seatWaitlistCustomer: (id: number) => Promise<WaitlistEntry>;
  removeFromWaitlist: (id: number, reason?: string) => Promise<WaitlistEntry>;

  // Refresh functions
  refreshReservations: () => Promise<void>;
  refreshWaitlist: () => Promise<void>;
  refreshTableGroups: () => Promise<void>;
  refreshSettings: () => Promise<void>;
  refreshAll: () => Promise<void>;

  // Feature flags
  isReservationEnabled: boolean;
  isWaitlistEnabled: boolean;
  isTableMergeEnabled: boolean;
}

const ReservationContext = createContext<ReservationContextType | undefined>(undefined);

// ============================================================================
// HOOK
// ============================================================================

export const useReservations = () => {
  const context = useContext(ReservationContext);
  if (!context) {
    throw new Error('useReservations must be used within a ReservationProvider');
  }
  return context;
};

// ============================================================================
// PROVIDER
// ============================================================================

interface ReservationProviderProps {
  children: ReactNode;
}

export const ReservationProvider: React.FC<ReservationProviderProps> = ({ children }) => {
  const { user, isVendor, isSteward } = useAuth();
  const { on, off, isConnected } = useSocket();

  // State
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [tableGroups, setTableGroups] = useState<TableGroup[]>([]);
  const [settings, setSettings] = useState<ReservationSettings | null>(null);
  const [dailyStats, setDailyStats] = useState<ReservationDailyStats | null>(null);
  const [waitlistStats, setWaitlistStats] = useState<WaitlistStats | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(
    todayLocalIso()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Feature flags derived from settings
  const isReservationEnabled = settings?.isReservationEnabled ?? false;
  const isWaitlistEnabled = settings?.isWaitlistEnabled ?? false;
  const isTableMergeEnabled = settings?.isTableMergeEnabled ?? false;

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchReservations = useCallback(async () => {
    try {
      const [reservationsData, statsData] = await Promise.all([
        reservationApi.getByDate(selectedDate),
        reservationApi.getDailyStats(selectedDate),
      ]);
      setReservations(reservationsData);
      setDailyStats(statsData);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      throw err;
    }
  }, [selectedDate]);

  const fetchWaitlist = useCallback(async () => {
    try {
      const [waitlistData, statsData] = await Promise.all([
        waitlistApi.getAll(),
        waitlistApi.getStats(),
      ]);
      setWaitlist(waitlistData);
      setWaitlistStats(statsData);
    } catch (err) {
      console.error('Error fetching waitlist:', err);
      throw err;
    }
  }, []);

  const fetchTableGroups = useCallback(async () => {
    try {
      const data = await tableGroupApi.getActive();
      setTableGroups(data);
    } catch (err) {
      console.error('Error fetching table groups:', err);
      throw err;
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await reservationSettingsApi.get();
      setSettings(data);
    } catch (err) {
      console.error('Error fetching reservation settings:', err);
      throw err;
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchSettings(),
        fetchReservations(),
        fetchWaitlist(),
        fetchTableGroups(),
      ]);
    } catch (err) {
      setError((err as Error).message || 'Failed to load reservation data');
    } finally {
      setLoading(false);
    }
  }, [fetchSettings, fetchReservations, fetchWaitlist, fetchTableGroups]);

  // Initial load
  useEffect(() => {
    if (user && (isVendor || isSteward)) {
      refreshAll();
    }
  }, [user, isVendor, isSteward]);

  // Refresh when date changes
  useEffect(() => {
    if (user && (isVendor || isSteward)) {
      fetchReservations().catch((err) => {
        setError((err as Error).message || 'Failed to load reservations');
      });
    }
  }, [selectedDate, user, isVendor, isSteward, fetchReservations]);

  // ============================================================================
  // SOCKET.IO EVENT HANDLERS
  // ============================================================================

  useEffect(() => {
    if (!isConnected) return;

    // Reservation events
    const handleReservationNew = (data: Reservation) => {
      if (data.reservationDate === selectedDate) {
        setReservations((prev) => [...prev, data]);
        fetchReservations(); // Refresh stats
      }
    };

    const handleReservationUpdated = (data: Reservation) => {
      setReservations((prev) =>
        prev.map((r) => (r.id === data.id ? data : r))
      );
      if (data.reservationDate === selectedDate) {
        fetchReservations(); // Refresh stats
      }
    };

    const handleReservationCancelled = (data: { id: number }) => {
      setReservations((prev) =>
        prev.map((r) =>
          r.id === data.id ? { ...r, status: 'CANCELLED' as const } : r
        )
      );
      fetchReservations(); // Refresh stats
    };

    const handleReservationSeated = (data: Reservation) => {
      setReservations((prev) =>
        prev.map((r) => (r.id === data.id ? data : r))
      );
      fetchReservations(); // Refresh stats
    };

    // Table group events
    const handleTableGroupCreated = (data: TableGroup) => {
      setTableGroups((prev) => [...prev, data]);
    };

    const handleTableGroupClosed = (data: { id: number }) => {
      setTableGroups((prev) => prev.filter((g) => g.id !== data.id));
    };

    // Waitlist events
    const handleWaitlistAdded = (data: WaitlistEntry) => {
      setWaitlist((prev) => [...prev, data]);
      fetchWaitlist(); // Refresh stats
    };

    const handleWaitlistNotified = (data: WaitlistEntry) => {
      setWaitlist((prev) =>
        prev.map((w) => (w.id === data.id ? data : w))
      );
    };

    const handleWaitlistSeated = (data: WaitlistEntry) => {
      setWaitlist((prev) => prev.filter((w) => w.id !== data.id));
      fetchWaitlist(); // Refresh stats
    };

    const handleWaitlistRemoved = (data: { id: number }) => {
      setWaitlist((prev) => prev.filter((w) => w.id !== data.id));
      fetchWaitlist(); // Refresh stats
    };

    // Register listeners
    on(RESERVATION_EVENTS.NEW, handleReservationNew);
    on(RESERVATION_EVENTS.UPDATED, handleReservationUpdated);
    on(RESERVATION_EVENTS.CANCELLED, handleReservationCancelled);
    on(RESERVATION_EVENTS.SEATED, handleReservationSeated);
    on(TABLE_GROUP_EVENTS.CREATED, handleTableGroupCreated);
    on(TABLE_GROUP_EVENTS.CLOSED, handleTableGroupClosed);
    on(WAITLIST_EVENTS.ADDED, handleWaitlistAdded);
    on(WAITLIST_EVENTS.NOTIFIED, handleWaitlistNotified);
    on(WAITLIST_EVENTS.SEATED, handleWaitlistSeated);
    on(WAITLIST_EVENTS.REMOVED, handleWaitlistRemoved);

    // Cleanup
    return () => {
      off(RESERVATION_EVENTS.NEW, handleReservationNew);
      off(RESERVATION_EVENTS.UPDATED, handleReservationUpdated);
      off(RESERVATION_EVENTS.CANCELLED, handleReservationCancelled);
      off(RESERVATION_EVENTS.SEATED, handleReservationSeated);
      off(TABLE_GROUP_EVENTS.CREATED, handleTableGroupCreated);
      off(TABLE_GROUP_EVENTS.CLOSED, handleTableGroupClosed);
      off(WAITLIST_EVENTS.ADDED, handleWaitlistAdded);
      off(WAITLIST_EVENTS.NOTIFIED, handleWaitlistNotified);
      off(WAITLIST_EVENTS.SEATED, handleWaitlistSeated);
      off(WAITLIST_EVENTS.REMOVED, handleWaitlistRemoved);
    };
  }, [isConnected, selectedDate, on, off, fetchReservations, fetchWaitlist]);

  // ============================================================================
  // RESERVATION ACTIONS
  // ============================================================================

  const createReservation = useCallback(async (data: ReservationRequest): Promise<Reservation> => {
    const reservation = await reservationApi.create(data);
    // Explicitly refresh to ensure UI updates (don't rely solely on socket)
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  const updateReservation = useCallback(async (
    id: number,
    data: Partial<ReservationRequest>
  ): Promise<Reservation> => {
    const reservation = await reservationApi.update(id, data);
    // Explicitly refresh to ensure UI updates (don't rely solely on socket)
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  const confirmReservation = useCallback(async (id: number): Promise<Reservation> => {
    const reservation = await reservationApi.confirm(id);
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  const seatReservation = useCallback(async (id: number): Promise<Reservation> => {
    const reservation = await reservationApi.seat(id);
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  const completeReservation = useCallback(async (id: number): Promise<Reservation> => {
    const reservation = await reservationApi.complete(id);
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  const cancelReservation = useCallback(async (id: number, reason?: string): Promise<Reservation> => {
    const reservation = await reservationApi.cancel(id, reason);
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  const markNoShow = useCallback(async (id: number): Promise<Reservation> => {
    const reservation = await reservationApi.markNoShow(id);
    await fetchReservations();
    return reservation;
  }, [fetchReservations]);

  // ============================================================================
  // TABLE GROUP ACTIONS
  // ============================================================================

  const createTableGroup = useCallback(async (data: TableGroupRequest): Promise<TableGroup> => {
    const group = await tableGroupApi.create(data);
    // Manually add to state in case socket is delayed
    setTableGroups((prev) => {
      // Avoid duplicates if socket already added
      if (prev.some((g) => g.id === group.id)) return prev;
      return [...prev, group];
    });
    return group;
  }, []);

  const closeTableGroup = useCallback(async (groupId: number): Promise<TableGroup> => {
    const group = await tableGroupApi.close(groupId);
    // Manually remove from state in case socket is delayed
    setTableGroups((prev) => prev.filter((g) => g.id !== groupId));
    return group;
  }, []);

  const addTableToGroup = useCallback(async (
    groupId: number,
    tableId: number
  ): Promise<TableGroup> => {
    const group = await tableGroupApi.addTable(groupId, tableId);
    setTableGroups((prev) =>
      prev.map((g) => (g.id === groupId ? group : g))
    );
    return group;
  }, []);

  const removeTableFromGroup = useCallback(async (
    groupId: number,
    tableId: number
  ): Promise<TableGroup> => {
    const group = await tableGroupApi.removeTable(groupId, tableId);
    setTableGroups((prev) =>
      prev.map((g) => (g.id === groupId ? group : g))
    );
    return group;
  }, []);

  const linkBillingToGroup = useCallback(async (
    groupId: number,
    orderLineId: number
  ): Promise<TableGroup> => {
    const group = await tableGroupApi.linkBilling(groupId, orderLineId);
    setTableGroups((prev) =>
      prev.map((g) => (g.id === groupId ? group : g))
    );
    return group;
  }, []);

  // ============================================================================
  // WAITLIST ACTIONS
  // ============================================================================

  const addToWaitlist = useCallback(async (data: WaitlistRequest): Promise<WaitlistEntry> => {
    const entry = await waitlistApi.add(data);
    await fetchWaitlist();
    return entry;
  }, [fetchWaitlist]);

  const notifyWaitlistCustomer = useCallback(async (
    id: number,
    tableId?: number,
    tableGroupId?: number
  ): Promise<WaitlistEntry> => {
    const entry = await waitlistApi.notify(id, tableId, tableGroupId);
    await fetchWaitlist();
    return entry;
  }, [fetchWaitlist]);

  const seatWaitlistCustomer = useCallback(async (id: number): Promise<WaitlistEntry> => {
    const entry = await waitlistApi.seat(id);
    await fetchWaitlist();
    return entry;
  }, [fetchWaitlist]);

  const removeFromWaitlist = useCallback(async (
    id: number,
    reason?: string
  ): Promise<WaitlistEntry> => {
    const entry = await waitlistApi.remove(id, reason);
    await fetchWaitlist();
    return entry;
  }, [fetchWaitlist]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const contextValue: ReservationContextType = useMemo(
    () => ({
      // State
      reservations,
      waitlist,
      tableGroups,
      settings,
      dailyStats,
      waitlistStats,
      selectedDate,
      loading,
      error,

      // Reservation actions
      setSelectedDate,
      createReservation,
      updateReservation,
      confirmReservation,
      seatReservation,
      completeReservation,
      cancelReservation,
      markNoShow,

      // Table group actions
      createTableGroup,
      closeTableGroup,
      addTableToGroup,
      removeTableFromGroup,
      linkBillingToGroup,

      // Waitlist actions
      addToWaitlist,
      notifyWaitlistCustomer,
      seatWaitlistCustomer,
      removeFromWaitlist,

      // Refresh functions
      refreshReservations: fetchReservations,
      refreshWaitlist: fetchWaitlist,
      refreshTableGroups: fetchTableGroups,
      refreshSettings: fetchSettings,
      refreshAll,

      // Feature flags
      isReservationEnabled,
      isWaitlistEnabled,
      isTableMergeEnabled,
    }),
    [
      reservations,
      waitlist,
      tableGroups,
      settings,
      dailyStats,
      waitlistStats,
      selectedDate,
      loading,
      error,
      createReservation,
      updateReservation,
      confirmReservation,
      seatReservation,
      completeReservation,
      cancelReservation,
      markNoShow,
      createTableGroup,
      closeTableGroup,
      addTableToGroup,
      removeTableFromGroup,
      linkBillingToGroup,
      addToWaitlist,
      notifyWaitlistCustomer,
      seatWaitlistCustomer,
      removeFromWaitlist,
      fetchReservations,
      fetchWaitlist,
      fetchTableGroups,
      fetchSettings,
      refreshAll,
      isReservationEnabled,
      isWaitlistEnabled,
      isTableMergeEnabled,
    ]
  );

  return (
    <ReservationContext.Provider value={contextValue}>
      {children}
    </ReservationContext.Provider>
  );
};

export default ReservationContext;
